//#region types
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { RevenueReportingFormTypes } from "models/formTypes";
//#endregion
import useAPIClientHelper from "./useAPIClientHelper";

function useRevenueReportingService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getTemplates = (options: { showErrors?: boolean } = { showErrors: true }) => {
    return apiClientInstance(AuthEnums.AuthType.Company, options)
      .RevenueReportingClient.GetTemplates()
      .then((res) => res.data);
  };

  const submitReport = (report: ApiModels.RevenueReportDto, options?: { hideErrorsForCodes?: string[] }) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true, ...options })
      .RevenueReportingClient.Post(report)
      .then((res) => res.data);
  };

  const getReports = (filter: ApiModels.RevenueReportFilter, options?: { showErrors?: boolean }) => {
    return apiClientInstance(AuthEnums.AuthType.Company, options).RevenueReportingClient.Get(filter);
  };

  const getReport = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RevenueReportingClient.GetById(id)
      .then((res) => res.data);
  };

  const getAttachment = (reportId: string, attachmentId?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RevenueReportingClient.GetAttachment(reportId, attachmentId)
      .then((res) => res.data);
  };

  const getTransactions = (reportId: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RevenueReportingClient.GetTransactions(reportId)
      .then((res) => res.data);
  };

  const approve = (request: ApiModels.RevenueReportApproveRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .RevenueReportingClient.Approve(request)
      .then((res) => res.data);
  };

  const getApprovals = (templateId?: string, year?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RevenueReportingClient.GetApprovals(templateId, year)
      .then((res) => res.data);
  };

  const getApprovalAttachment = (templateId: string, periodYear: number, periodMonth: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .RevenueReportingClient.GetApprovalAttachment(templateId, periodYear, periodMonth)
      .then((res) => res.data);
  };

  const reportToAPIFilter = (
    formFilter?: RevenueReportingFormTypes.Filter
  ): Omit<ApiModels.RevenueReportFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      templateId: formFilter?.templateId,
      status: formFilter?.status,
      fromDate: formFilter?.date?.start,
      toDate: formFilter?.date?.end,
      ownerIds: formFilter?.registeredById ? [formFilter.registeredById] : [],
      minAmount: formFilter?.saleAmount?.lowest,
      maxAmount: formFilter?.saleAmount?.highest,
      minDifference: formFilter?.differenceAmount?.lowest,
      maxDifference: formFilter?.differenceAmount?.highest,
    };
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: RevenueReportingFormTypes.Filter) => {
    return getReports({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  return {
    getTemplates,
    submitReport,
    getReports,
    getReport,
    getAttachment,
    getTransactions,
    approve,
    getApprovals,
    getApprovalAttachment,
    reportToAPIFilter,
    reportFilter,
  };
}

export default useRevenueReportingService;
